import React, { useCallback, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import VerifiedIcon from "@mui/icons-material/Verified";
import CertificationShell from "../CertificationShell";
import AhaEcardsSection from "../../aha/AhaEcardsSection";
import StatusTiles from "./StatusTiles";
import ValidityTimeline from "./ValidityTimeline";
import { STATUS_STYLES } from "./statusStyles";
import { buildCertificationsModel, selectRows } from "./buildCertificationsModel";

function EmptyState({ tab, filtered, onUpload }) {
  const message = tab === "renewals"
    ? "Nothing to renew. None of your certifications has expired."
    : filtered
      ? "No certifications match this status."
      : "You have no certifications on file yet.";
  return (
    <Box data-testid="cert-empty" sx={{ textAlign: "center", py: 8, border: "1px dashed var(--color-hairline-cloud, #e5e7eb)", borderRadius: "12px" }}>
      <VerifiedIcon aria-hidden sx={{ fontSize: 44, mb: 1, color: "text.disabled" }} />
      <Typography sx={{ fontWeight: 600 }}>{message}</Typography>
      {tab === "earned" && !filtered && (
        <Button onClick={onUpload} sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}>Upload a certification</Button>
      )}
    </Box>
  );
}

/**
 * Student "My Certifications" screen (Student Certifications v3 design).
 * Presentational: the parent owns data loading, dialogs and the action handlers.
 */
export default function StudentCertificationsView({
  certs, requiredTypes, missingTypeIds, completions, error, onDismissError,
  onUpload, onOpenDetails, onDownloadDocument, onDownloadCompletion, onCopyLink, children,
}) {
  const [tab, setTab] = useState("earned");
  const [statusFilter, setStatusFilter] = useState(null);
  const [notice, setNotice] = useState("");

  const model = useMemo(
    () => buildCertificationsModel({ certs, requiredTypes, missingTypeIds, completions }),
    [certs, requiredTypes, missingTypeIds, completions],
  );
  const { rows, pins } = selectRows(model, tab, null);

  const changeTab = useCallback((next) => { setTab(next); setStatusFilter(null); }, []);
  const toggleTile = useCallback((id) => setStatusFilter((current) => (current === id ? null : id)), []);

  const copyLink = useCallback(async (row) => {
    try {
      await onCopyLink(row.shareUrl);
      setNotice("Verified link copied to clipboard");
    } catch {
      setNotice("Could not copy the link. Copy it from the document page instead.");
    }
  }, [onCopyLink]);

  const handlers = {
    onDownload: (row) => onDownloadDocument(row.cert),
    onShare: copyLink,
    onRenew: (row) => onOpenDetails(row.certType, row.cert),
    onUpload: (row) => onUpload(row.certType),
    onDownloadCompletion,
  };

  const tabs = [
    { id: "earned", label: "Earned", count: model.earnedCount, testId: "cert-tab-earned" },
    { id: "renewals", label: "Renewals", count: model.renewalsCount, testId: "cert-tab-renewals" },
  ];

  return (
    <CertificationShell
      title="My Certifications"
      description="Certificates you have earned from completed courses. Download a PDF, share a verified link, or renew before expiry."
      action={(
        <Button
          variant="contained"
          startIcon={<FileUploadIcon sx={{ fontSize: 18 }} />}
          onClick={() => onUpload(null)}
          sx={{ height: 40, borderRadius: "11px", textTransform: "none", fontWeight: 700, px: 2.25, backgroundImage: "none" }}
        >
          Upload
        </Button>
      )}
      tabs={tabs}
      activeTab={tab}
      onTabChange={changeTab}
    >
      {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: "11px" }} onClose={onDismissError}>{error}</Alert>}

      <StatusTiles counts={model.counts} selected={statusFilter} onToggle={toggleTile} showMissing={model.hasRequirements} />

      {tab === "earned" && <AhaEcardsSection />}

      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5, mb: 1.75 }}>
        <Typography component="h2" sx={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600, letterSpacing: "-.01em" }}>{tab === "renewals" ? "Renewals" : "Validity timeline"}</Typography>
        <Typography data-testid="cert-timeline-meta" sx={{ fontSize: 13, color: "var(--color-accent-violet-mid, #6b7280)" }}>
          {statusFilter ? `Highlighting ${STATUS_STYLES[statusFilter].tile.toLowerCase()} · click again to clear` : tab === "renewals" ? "Certifications past expiry" : `${model.axis.startYear} – ${model.axis.endYear}`}
        </Typography>
      </Box>
      {rows.length === 0 && pins.length === 0 ? (
        <EmptyState tab={tab} filtered={Boolean(statusFilter)} onUpload={() => onUpload(null)} />
      ) : (
        <ValidityTimeline rows={rows} pins={pins} axis={model.axis} handlers={handlers} statusFilter={statusFilter} />
      )}

      {children}
      <Snackbar open={Boolean(notice)} autoHideDuration={3500} onClose={() => setNotice("")} message={notice} />
    </CertificationShell>
  );
}
